import { Box, Flex, List, ListItem, BoxProps } from '@chakra-ui/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper';
import 'swiper/css';
import 'swiper/css/pagination';
import Image from 'next/image';

const JobCard = ({
  job,
  ...props
}: {
  job: {
    icon: string;
    title: string;
    industry: string;
    techStack: string[];
    salary?: string;
    description: string;
    color: string;
    bgColor: string;
  };
}) => {
  return (
    <Box
      p="8"
      boxShadow="-0.0625rem 0.25rem 1rem rgba(0, 0, 0, 0.12)"
      bgColor="white"
      borderRadius="1rem"
      minH="25.25rem"
      {...props}
    >
      <Flex alignItems="flex-start" justifyContent="space-between">
        <Image
          src={job.icon}
          alt=""
          width="52"
          height="52"
          style={{ objectFit: 'contain' }}
          layout="intrinsic"
        />
        {job.salary && (
          <Box
            fontSize="0.875rem"
            lineHeight="1rem"
            fontWeight="medium"
            bgColor="grey.100"
            textAlign="center"
            px="4"
            py="2"
            borderRadius="2.5625rem"
          >
            {job.salary}
          </Box>
        )}
      </Flex>
      <Box mt="4" fontSize="1.75rem" lineHeight="1" fontWeight="semibold">
        {job.title}
      </Box>
      <Box fontSize="1.125rem" lineHeight="1.5rem">
        <Box as="span" fontWeight="700">
          Industry:
        </Box>{' '}
        {job.industry}
      </Box>
      <List mt="4">
        {job.techStack.map((item, index) => (
          <ListItem
            key={index}
            display="inline-block"
            mb="3"
            mr="3"
            py="0.625rem"
            px="4"
            color={job.color}
            bgColor={job.bgColor}
            borderRadius="2.5625rem"
            fontWeight="500"
            fontSize="sm"
            lineHeight="1rem"
          >
            {item}
          </ListItem>
        ))}
      </List>
      <Box fontSize="1rem" lineHeight="1.25rem">
        {job.description}
      </Box>
    </Box>
  );
};

interface JobProps extends BoxProps {
  jobs: {
    icon: string;
    title: string;
    industry: string;
    techStack: string[];
    salary?: string;
    description: string;
    color: string;
    bgColor: string;
  }[];
}

export default function JobSlider({ jobs, ...props }: JobProps) {
  const pagination = {
    clickable: true,
    renderBullet: function (index, className) {
      return '<span class="' + className + '"></span>';
    },
  };
  return (
    <Box id="available-jobs" my={{ base: '12', lg: '20' }} {...props}>
      <Box display={{ base: 'block', md: 'block' }}>
        <style global jsx>{`
          .swiper-horizontal .swiper-pagination {
            bottom: 0;
          }
          .swiper-pagination-horizontal.swiper-pagination-bullets .swiper-pagination-bullet {
            width: 1rem;
            height: 1rem;
            margin: 0 0.5rem;
            background-color: #43b97f;
          }
        `}</style>
        <Box
          as={Swiper}
          py="4"
          pl={{
            base: 'calc(50% - 180px)',
            md: 'calc(50% - 350px)',
            lg: 'calc(50% - 460px)',
            xl: 'calc(50% - 550px)',
          }}
          pr="6"
          pb="16"
          slidesPerView={'auto'}
          pagination={pagination}
          modules={[Pagination, Autoplay]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
          }}
          speed={1000}
          autoHeight={true}
          spaceBetween={32}
        >
          {jobs.map((job, index) => (
            <SwiperSlide key={index} style={{ width: '20.8125rem' }}>
              <JobCard job={job} />
            </SwiperSlide>
          ))}
        </Box>
      </Box>
    </Box>
  );
}
